import { ApiError, AUTH_ERROR_CODES, readWidgetRedirect, stripWidgetParams } from '@getslot/api-client'
import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { session } from '../api/session'
import { getInitData, signalReady } from '../telegram/web-app'

export type AuthStatus = 'loading' | 'guest' | 'beta_closed' | 'unavailable' | 'ready'

interface AuthValue {
  status: AuthStatus
  retry: () => void
}

const AuthContext = createContext<AuthValue | null>(null)

async function login(): Promise<boolean> {
  const initData = getInitData()
  if (initData !== null) {
    await session.loginWithTelegram(initData)
    return true
  }
  // вход с компьютера: Login Widget вернул данные в query (data-auth-url)
  const widgetUser = readWidgetRedirect(window.location.search)
  if (widgetUser === null) return false
  window.history.replaceState(null, '', window.location.pathname + stripWidgetParams(window.location.search))
  await session.loginWithWidget(widgetUser)
  return true
}

async function signIn(): Promise<AuthStatus> {
  try {
    return (await login()) ? 'ready' : 'guest'
  } catch (error) {
    if (error instanceof ApiError && error.code === AUTH_ERROR_CODES.betaClosed) return 'beta_closed'
    if (error instanceof ApiError && !error.isUnavailable && error.status < 500) return 'guest'
    return 'unavailable'
  }
}

/**
 * Вход при запуске: в Telegram по initData, в браузере — после редиректа Telegram Login Widget.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const queryClient = useQueryClient()

  const retry = useCallback(() => {
    setStatus('loading')
    void signIn().then(setStatus)
  }, [])

  useEffect(() => {
    signalReady()
    void signIn().then(setStatus)
    return session.onExpired(() => {
      queryClient.clear()
      setStatus('guest')
    })
  }, [queryClient])

  const value = useMemo(() => ({ status, retry }), [status, retry])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (value === null) throw new Error('useAuth вне AuthProvider')
  return value
}
